import React from 'react';
import { useSelector } from 'react-redux';

import { useIsPrimeAuthUser } from 'auth/hooks';
import { filterUniqueRamcoAuthProfiles } from 'hooks/domain/user/filterUniqueRamcoAuthProfiles';
import useOptInToLibrariesMutation from 'hooks/domain/user/mutations/useOptInToLibrariesMutation';
import usePopulateRamcoUserInformationMutation from 'hooks/domain/user/mutations/usePopulateRamcoUserInformationMutation';
import useGetUserSettingsQuery from 'hooks/domain/user/queries/useGetUserSettingsQuery';
import useIsAuthorizedForLibraries from 'hooks/domain/user/useIsAuthorizedForLibraries';

import { targetOptInLibrariesSelector } from '../store/selectors';

export function useOptInToAuthorizedLibs() {
  const isPrimeUser = useIsPrimeAuthUser();

  const targetOptInLibraries = useSelector(targetOptInLibrariesSelector);
  const authorizedLibs = useIsAuthorizedForLibraries(targetOptInLibraries);

  const userSettings = useGetUserSettingsQuery();
  const optInToLibraries = useOptInToLibrariesMutation();
  const populateRamcoUserInfo = usePopulateRamcoUserInformationMutation();

  const isLoading =
    authorizedLibs.isLoading || userSettings.isLoading || optInToLibraries.isLoading || populateRamcoUserInfo.isLoading;

  const optInToAuthorizedLibs = React.useCallback(async () => {
    if (!userSettings.data) return;
    if (optInToLibraries.isSuccess) return;

    try {
      await optInToLibraries.mutateAsync(
        authorizedLibs.authorized.map((l) => l.id).map((id) => ({ userId: userSettings.data.id, libraryId: id }))
      );

      if (!isPrimeUser) {
        const authorizedAuthProfiles = authorizedLibs.authorized.flatMap((l) => l.authProfiles);
        const ramcoAuthProfiles = filterUniqueRamcoAuthProfiles(authorizedAuthProfiles);
        // Do we still need this call if ramcoAuthProfiles is empty?
        await populateRamcoUserInfo.mutateAsync({
          userProfileId: userSettings.data.id,
          libraryAuthProfileTypes: ramcoAuthProfiles,
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, [authorizedLibs.authorized, isPrimeUser, optInToLibraries, populateRamcoUserInfo, userSettings.data]);

  return React.useMemo(() => ({ optInToAuthorizedLibs, isLoading }), [optInToAuthorizedLibs, isLoading]);
}
